import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { useCart } from "@/context/CartContext";
import { api, imgUrl, usd } from "@/lib/api";
import { useLang } from "@/i18n";

export default function FeaturedProducts() {
  const [products, setProducts] = useState(null);
  const { add } = useCart();
  const navigate = useNavigate();
  const { t } = useLang();

  useEffect(() => {
    api
      .get("/products", { params: { featured: true } })
      .then((r) => setProducts(r.data.slice(0, 4)))
      .catch(() => setProducts([]));
  }, []);

  return (
    <section
      id="featured"
      data-testid="featured-products-section"
      className="bg-[#0F172A] px-6 md:px-12 lg:px-24 py-20 lg:py-32"
    >
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <Reveal>
          <p className="text-xs tracking-[0.25em] uppercase text-[#ED1C24] mb-4">
            {t("featured_label")}
          </p>
          <h2 className="font-display font-semibold text-white text-4xl sm:text-5xl tracking-tight">
            {t("featured_title").replace(".", "")}<span className="text-[#FFC61A]">.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="flex flex-wrap items-center gap-5">
            <button
              data-testid="view-full-shop-btn"
              onClick={() => navigate("/shop")}
              className="group inline-flex items-center gap-2 text-sm text-slate-300 hover:text-[#FFC61A] transition-colors duration-300 border-b border-white/20 hover:border-[#FFC61A] pb-1"
            >
              {t("featured_view")}
              <ArrowUpRight
                size={16}
                className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300"
              />
            </button>
          </div>
        </Reveal>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {(products ?? Array.from({ length: 4 })).map((p, i) =>
          p ? (
            <Reveal key={p.id} delay={i * 0.08}>
              <div
                data-testid={`featured-product-${p.id}`}
                className="group bg-[#0B1120] border border-white/10 hover:border-[#ED1C24]/60 hover:-translate-y-2 transition-[border-color,transform] duration-500"
              >
                <div className="relative h-52 overflow-hidden">
                  <Link to={`/product/${p.id}`}>
                    <img
                      src={imgUrl(p.image)}
                      alt={p.name}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                  </Link>
                  <span className="absolute top-3 left-3 bg-[#ED1C24] text-white text-[10px] tracking-[0.15em] uppercase px-3 py-1">
                    {t("featured_tag")}
                  </span>
                </div>
                <div className="p-5">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-2">
                    {p.category}{p.brand ? ` · ${p.brand}` : ""}
                  </p>
                  <h3 className="font-display font-medium text-white text-base leading-snug min-h-[2.6rem]">
                    <Link to={`/product/${p.id}`} className="hover:text-[#FFC61A] transition-colors duration-300">
                      {p.name}
                    </Link>
                  </h3>
                  <div className="flex items-center justify-between mt-4">
                    <span className="font-display text-xl text-[#FFC61A]">
                      {usd(p.price)}
                    </span>
                    <button
                      data-testid={`featured-add-to-cart-${p.id}`}
                      onClick={() => {
                        add(p);
                        toast.success(`${p.name} ${t("cart_added")}`);
                      }}
                      className="inline-flex items-center gap-2 bg-[#ED1C24] hover:bg-[#C81016] text-white text-xs font-medium px-4 py-2.5 transition-colors duration-300"
                      aria-label={`Add ${p.name} to cart`}
                    >
                      <ShoppingCart size={14} />
                      {t("add")}
                    </button>
                  </div>
                </div>
              </div>
            </Reveal>
          ) : (
            <div
              key={`skeleton-${i}`}
              className="h-80 bg-white/[0.03] border border-white/10 animate-pulse"
            />
          )
        )}
      </div>
    </section>
  );
}
